import { motDuCompte, motDuRetour, retourDuPanneau, type PanneauDuPouce } from './panneauDuPouce';
import { AideAuPouce } from '../aide';
import { OngletIABranchee } from '../comptes';
import { HautDuPouce } from '../partage/HautDuPouce';
import { PanneauDesNotifications } from './PanneauDesNotifications';
import {
  AIDE,
  CLAUDE_BRANCHE,
  GESTES_DU_TELEPHONE,
  L_APP,
  MON_COMPTE,
  MON_PROFIL,
  NOTIFICATIONS,
  PARAMETRES,
  SORTIR,
} from '../partage/vocabulaire';
import './ParametresDuTelephone.css';

/**
 * Les Paramètres de l'app au téléphone (ticket 01 du chantier `telephone`), ouverts par
 * l'initiale en haut de chaque écran, en papier comme la maquette du carnet (chantier
 * telephone-habit, 02, `app-au-telephone.html`, `reglages()`) : le retour, « l'app » au-dessus
 * de « Paramètres », puis des lignes : Mon profil et le prénom (chantier telephone-profil, 01),
 * Claude branché, Notifications (chantier notifications-socle, ticket 03), Aide, Mon compte
 * (l'e-mail), Sortir en rouge. Pas l'ambiance (décision 3 d'Igor : le Carnet seul au téléphone).
 *
 * Au téléphone, il n'y a pas le tuto de l'ordinateur : l'Aide vit ici, un geste du téléphone à
 * la fois (ticket 14). Claude branché montre l'onglet « IA branchée » de l'ordinateur (les clés
 * d'accès et les connexions de Claude), tel quel.
 *
 * Couché (`couche`, chantier telephone-couche-suite, 05, maquette `cReglages()`) : le haut sur une
 * ligne, le retour et le titre, puis les mêmes lignes en deux colonnes ; l'Aide, Claude branché
 * et les Notifications s'ouvrent pareil, dans le cadre couché.
 *
 * Le rendu seul : ce qui s'ouvre et où ramène le retour est dans `panneauDuPouce.ts`.
 */
export function ParametresDuTelephone({
  panneau,
  email,
  prenom,
  onAller,
  onProfil,
  onSortir,
  couche = false,
}: {
  panneau: Exclude<PanneauDuPouce, null>;
  email: string | null;
  /** Le prénom du Profil, à droite de « Mon profil ». */
  prenom: string | null;
  onAller: (panneau: PanneauDuPouce) => void;
  /** « Mon profil » : la page Profil du téléphone, une adresse et non un panneau (elle tient quand le téléphone tourne). */
  onProfil: () => void;
  onSortir: () => void;
  /** Le téléphone couché : les lignes en deux colonnes. */
  couche?: boolean;
}) {
  const retour = { nom: motDuRetour(panneau), onRetour: () => onAller(retourDuPanneau(panneau)) };
  const nom =
    panneau === 'aide' ? AIDE : panneau === 'claude' ? CLAUDE_BRANCHE : panneau === 'notifications' ? NOTIFICATIONS : PARAMETRES;

  return (
    <section className={`parametres-tel${couche ? ' parametres-tel--couche' : ''}`} aria-label={nom}>
      <HautDuPouce
        titre={nom}
        retour={retour}
        couche={couche}
        dessus={panneau === 'parametres' ? <span className="parametres-tel-app">{L_APP}</span> : PARAMETRES}
      />

      {panneau === 'aide' && <AideAuPouce onCompris={() => onAller('parametres')} />}
      {panneau === 'claude' && (
        <div className="parametres-tel-claude">
          <OngletIABranchee />
        </div>
      )}
      {panneau === 'notifications' && (
        <div className="parametres-tel-claude">
          <PanneauDesNotifications />
        </div>
      )}
      {panneau === 'parametres' && (
        <div className="parametres-tel-menu">
          <button type="button" className="pouce-ligne-reg" data-ligne="profil" onClick={onProfil}>
            {MON_PROFIL}
            <span>{prenom ? `${prenom} ›` : '›'}</span>
          </button>
          <button type="button" className="pouce-ligne-reg" onClick={() => onAller('claude')}>
            {CLAUDE_BRANCHE}
            <span aria-hidden="true">›</span>
          </button>
          <button type="button" className="pouce-ligne-reg" onClick={() => onAller('notifications')}>
            {NOTIFICATIONS}
            <span aria-hidden="true">›</span>
          </button>
          <button type="button" className="pouce-ligne-reg" onClick={() => onAller('aide')}>
            {AIDE}
            <span>{GESTES_DU_TELEPHONE} ›</span>
          </button>
          <div className="pouce-ligne-reg">
            {MON_COMPTE}
            <span title={email ?? undefined}>{motDuCompte(email)}</span>
          </div>
          <button type="button" className="pouce-ligne-reg pouce-ligne-reg--sortir" onClick={onSortir}>
            {SORTIR}
          </button>
        </div>
      )}
    </section>
  );
}
